import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, Button, Collapse, styled, SxProps, Theme, Typography, useTheme } from '@mui/material'
import { Context, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { AuthorizationType } from 'src/enums/auth'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'

interface EquipmentSectionLayoutProps {
  title: string
  icon: ReactNode
  buttonText?: string
  buttonIcon?: ReactNode
  onButtonClick?: () => void
  context?: Context<any> | null | undefined
  permissions?: number[]
  content: ReactNode
  showMoreContent?: ReactNode
  contentSubtitle?: string
  showMoreContentSubtitle?: boolean
  expandText?: string
  collapseText?: string
  contentContainerStyle?: React.CSSProperties
  isReadOnly?: boolean
  sx?: SxProps<Theme> | undefined
}

const EquipmentSectionLayout = ({
  title,
  icon,
  buttonText,
  buttonIcon,
  onButtonClick,
  context,
  permissions = [],
  content,
  showMoreContent,
  contentSubtitle,
  showMoreContentSubtitle,
  expandText,
  collapseText,
  contentContainerStyle,
  isReadOnly = false,
  sx,
}: EquipmentSectionLayoutProps) => {
  const { t } = useTranslation(['common'])
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const hasShowMore = Boolean(showMoreContent)
  const expandLabel = expandText ?? t('showMore', { ns: 'common' })
  const collapseLabel = collapseText ?? t('showLess', { ns: 'common' })

  return (
    <SectionContainer sx={sx}>
      <HeaderContainer>
        <Flex.Row alignItems="center" gap={1.5}>
          <IconContainer>{icon}</IconContainer>
          <HeaderTitle theme={theme}>{title}</HeaderTitle>
        </Flex.Row>
        {buttonText && onButtonClick && (
          <AuthorizationProvider permissions={permissions} Context={context} type={AuthorizationType.Disabled}>
            <ActionButton
              variant="contained"
              size="small"
              startIcon={buttonIcon}
              onClick={onButtonClick}
              theme={theme}
              disabled={isReadOnly}
            >
              {buttonText}
            </ActionButton>
          </AuthorizationProvider>
        )}
      </HeaderContainer>

      {contentSubtitle && <SubtitleText theme={theme}>{contentSubtitle}</SubtitleText>}
      <MainContentBox style={contentContainerStyle}>{content}</MainContentBox>

      {hasShowMore && (
        <ShowMoreButton
          disableRipple
          onClick={() => setIsExpanded(prev => !prev)}
          startIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          theme={theme}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </ShowMoreButton>
      )}

      {showMoreContentSubtitle && <SubtitleText theme={theme}>{showMoreContentSubtitle}</SubtitleText>}
      {hasShowMore && (
        <ContentBox>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ShowMoreSection>{showMoreContent}</ShowMoreSection>
          </Collapse>
        </ContentBox>
      )}
    </SectionContainer>
  )
}

export default EquipmentSectionLayout

// Styled Components
const SectionContainer = styled(Box)({
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '130px',
})

const HeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '5px',
})

const IconContainer = styled(Box)({
  border: '1px solid #e2e8f0',
  borderRadius: '4px',
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '18px',
  },
})

const HeaderTitle = styled(Typography)<{ theme: any }>(({ theme }) => ({
  fontSize: '15px',
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
  letterSpacing: '-0.23px',
}))

const ActionButton = styled(Button)<{ theme: any }>(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: 'white',
  fontSize: '12px',
  fontWeight: 500,
  height: '29px',
  textTransform: 'none',
  padding: '4.5px 10px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.primaryDark),
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    '& svg': {
      fontSize: '14px',
    },
  },
}))

const ContentBox = styled(Box)({
  overflowY: 'auto',
  flexShrink: 1,
  flexGrow: 0,
  minHeight: 0,
})

const MainContentBox = styled(ContentBox)({
  flexGrow: 1,
})

const ShowMoreSection = styled(Box)({
  marginTop: '12px',
  paddingTop: '12px',
})

const ShowMoreButton = styled(Button)<{ theme: any }>(({ theme }) => ({
  alignSelf: 'flex-start',
  marginTop: '8px',
  padding: '2px 0',
  minWidth: 'unset',
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    color: getThemeColor(theme, MUIThemeColors.primaryDark),
  },
  '& .MuiButton-endIcon': {
    marginLeft: '2px',
    '& svg': {
      fontSize: '16px',
    },
  },
}))

const SubtitleText = styled(Typography)<{ theme: any }>(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  letterSpacing: '-0.08px',
  marginTop: '5px',
}))
